import { useState, useRef, useEffect } from 'react';
import { createTranscriptionFromFile } from '@/api/transcriptions';
import type { UseVoiceRecordOptions, UseVoiceRecordReturn } from './useVoiceRecord';

interface RecordingSession {
  stream: MediaStream;
  recorder: MediaRecorder;
}

export const useVoiceRecordWeb = ({
  onTranscription,
  onError,
  onDictationAttempted,
}: UseVoiceRecordOptions): UseVoiceRecordReturn => {
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);

  const sessionRef = useRef<RecordingSession | null>(null);
  const shouldUploadRef = useRef(true);

  const endSession = () => {
    sessionRef.current?.stream.getTracks().forEach((t) => t.stop());
    sessionRef.current = null;
  };

  useEffect(() => () => endSession(), []);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // getUserMedia resolving means the user granted access
      onDictationAttempted?.({ permissionRequested: true, permissionGranted: true });
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4';
      const recorder = new MediaRecorder(stream, { mimeType });
      const chunks: Blob[] = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data);
      };

      recorder.onstop = async () => {
        endSession();
        setIsRecording(false);
        if (!shouldUploadRef.current) return;

        const blob = new Blob(chunks, { type: mimeType });
        if (blob.size === 0) {
          onError?.('No audio recorded');
          return;
        }

        setIsTranscribing(true);
        try {
          const ext = mimeType === 'audio/webm' ? 'webm' : 'mp4';
          const { text, fileId } = await createTranscriptionFromFile(blob, `recording.${ext}`);
          const trimmed = text.trim();
          if (trimmed) onTranscription(trimmed, fileId);
        } catch (err) {
          onError?.(err instanceof Error ? err.message : 'Transcription failed');
        } finally {
          setIsTranscribing(false);
        }
      };

      sessionRef.current = { stream, recorder };
      recorder.start();
      setIsRecording(true);
    } catch {
      // getUserMedia rejected — browser denied or user dismissed the prompt
      onDictationAttempted?.({ permissionRequested: true, permissionGranted: false });
      onError?.('Microphone access is needed to record');
    }
  };

  const toggleRecording = () => {
    if (isTranscribing) return;
    if (isRecording) {
      shouldUploadRef.current = true;
      sessionRef.current?.recorder.stop();
      return;
    }
    startRecording();
  };

  const stopRecording = (cancel = false) => {
    if (!isRecording) return;
    shouldUploadRef.current = !cancel;
    sessionRef.current?.recorder.stop();
  };

  return { isRecording, isTranscribing, toggleRecording, stopRecording };
};
